import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articleMap, getRelatedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = slug ? articleMap[slug] : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 内容导航平台" description="您访问的文章不存在或已被移除。" keywords="内容导航,文章" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <i className="ri-file-unknow-line text-6xl text-foreground-300 mb-4 block"></i>
              <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
              <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
              <button
                onClick={() => navigate('/')}
                className="px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </button>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const cat = categoryMap[article.categorySlug];
  const relatedArticles = getRelatedArticles(article.slug, article.categorySlug, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} - ${cat?.name || '内容分区'} | 内容导航平台`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={window.location.origin + (__BASE_PATH__ || '') + '/article/' + article.slug}
        ogType="article"
        ogImage={article.coverImage}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: window.location.origin + (__BASE_PATH__ || '') + '/',
            },
            ...(cat
              ? [
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: cat.name,
                    item: window.location.origin + (__BASE_PATH__ || '') + '/category/' + cat.slug,
                  },
                ]
              : []),
            {
              '@type': 'ListItem',
              position: cat ? 3 : 2,
              name: article.title,
            },
          ],
        }}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          datePublished: article.publishDate,
          publisher: {
            '@type': 'Organization',
            name: '内容导航平台',
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(cat ? [{ label: cat.name, href: `/category/${cat.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>

          <article className="max-w-4xl mx-auto px-4 md:px-6 pb-12">
            {/* Article Header */}
            <header className="mb-8">
              {cat && (
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-block text-xs font-medium text-primary-500 bg-primary-100/70 px-3 py-1 rounded-full mb-4 cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              )}
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读约{article.readTime}分钟
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount} 次阅读
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-stone max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
              style={{
                '--tw-prose-headings': 'oklch(var(--foreground-950))',
                '--tw-prose-body': 'oklch(var(--foreground-800))',
                '--tw-prose-links': 'oklch(var(--primary-500))',
                '--tw-prose-bold': 'oklch(var(--foreground-950))',
              } as React.CSSProperties}
            />

            {/* Tags */}
            {article.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-background-200/70">
                <h4 className="text-sm font-semibold text-foreground-950 mb-3">文章标签</h4>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag, i) => (
                    <span key={i} className="text-xs text-foreground-600 bg-background-100 px-3 py-1 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} {...rel} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}